import { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import BackLink from "@/components/BackLink";
import PendingDraftBanner from "@/components/PendingDraftBanner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const RUBRIKER = ["Hjälp i vardagen", "Hand mot hand", "Köp & sälj lokalt", "Evenemang"];

function modHeaders() {
  return { Authorization: `Bearer ${sessionStorage.getItem("mod_token")}` };
}

export default function ModCreate() {
  const navigate = useNavigate();
  // Etappe 4.3 — defaulta andrad_av till inloggad användare
  const user = JSON.parse(sessionStorage.getItem("mod_user") || "null");
  const [form, setForm] = useState({
    rubrik: "", titel: "", beskrivning: "", pristyp: "", prisbelopp: "",
    ort: "", avsandare_namn: "", kontakt_epost: "", kontakt_telefon: "", publish: true,
    andrad_av: (user?.name || user?.email || "").trim(),
    andringsnotering: "",
  });
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);

  const set = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors(prev => ({ ...prev, [field]: null }));
  };

  const validate = () => {
    const e = {};
    if (!form.rubrik) e.rubrik = "Välj en rubrik";
    if (!form.titel.trim()) e.titel = "Ange en titel";
    if (!form.beskrivning.trim()) e.beskrivning = "Ange en beskrivning";
    if (!form.avsandare_namn.trim()) e.avsandare_namn = "Ange avsändarens namn";
    if (form.rubrik === "Köp & sälj lokalt" && !form.pristyp) e.pristyp = "Ange pristyp";
    // Etappe 4.3 — Ändrad av Pflicht
    if (!form.andrad_av.trim()) e.andrad_av = "Ange namn på den som ändrar posten innan du sparar.";
    return e;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length > 0) { setErrors(errs); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/mod/anslag/create`, {
        rubrik: form.rubrik, titel: form.titel, beskrivning: form.beskrivning,
        pristyp: form.pristyp || null, prisbelopp: form.prisbelopp || null,
        ort: form.ort || null, avsandare_namn: form.avsandare_namn,
        kontakt_epost: form.kontakt_epost || null, kontakt_telefon: form.kontakt_telefon || null,
        publish: form.publish,
        andrad_av: form.andrad_av.trim(),
        andringsnotering: form.andringsnotering || null,
      }, { headers: modHeaders() });
      navigate("/mod");
    } catch (err) {
      if (err.response?.status === 401) navigate("/mod/login");
      else setErrors({ submit: err?.response?.data?.detail || "Något gick fel" });
    }
    setBusy(false);
  };

  return (
    <div data-testid="mod-create-page" className="max-w-2xl mx-auto px-4 sm:px-6 py-8">
      <BackLink to="/mod" label="Moderatorvy" />
      <PendingDraftBanner urlPattern="/api/mod/karttipps" />
      <h1 className="font-heading text-2xl font-medium text-ink mb-2">Skapa nytt anslag</h1>
      <p className="text-base text-muted-foreground mb-6">Skapa ett anslag direkt utan att gå via det publika formuläret.</p>

      <form onSubmit={handleSubmit} className="flex flex-col gap-5">
        <Field label="Rubrik" error={errors.rubrik} required>
          <select data-testid="mod-create-rubrik" value={form.rubrik} onChange={e => set("rubrik", e.target.value)} className="input-mod">
            <option value="">Välj rubrik...</option>
            {RUBRIKER.map(r => <option key={r} value={r}>{r}</option>)}
          </select>
        </Field>

        <Field label="Titel" error={errors.titel} required>
          <input data-testid="mod-create-titel" value={form.titel} onChange={e => set("titel", e.target.value)} className="input-mod" />
        </Field>

        <Field label="Beskrivning" error={errors.beskrivning} required>
          <textarea data-testid="mod-create-beskrivning" value={form.beskrivning} onChange={e => set("beskrivning", e.target.value)} rows={4} className="input-mod" />
        </Field>

        {form.rubrik === "Köp & sälj lokalt" && (
          <>
            <Field label="Pristyp" error={errors.pristyp} required>
              <select value={form.pristyp} onChange={e => set("pristyp", e.target.value)} className="input-mod">
                <option value="">Välj...</option>
                <option value="belopp">Ange pris</option>
                <option value="Skänkes">Skänkes</option>
                <option value="Bytes">Bytes</option>
                <option value="Överenskommelse">Överenskommelse</option>
              </select>
            </Field>
            {form.pristyp === "belopp" && (
              <Field label="Pris">
                <input value={form.prisbelopp} onChange={e => set("prisbelopp", e.target.value)} placeholder='T.ex. "500 kr"' className="input-mod" />
              </Field>
            )}
          </>
        )}

        <Field label="Ort / område">
          <input value={form.ort} onChange={e => set("ort", e.target.value)} placeholder="T.ex. Backe, Rossön" className="input-mod" />
        </Field>

        <Field label="Avsändarens namn" error={errors.avsandare_namn} required>
          <input data-testid="mod-create-namn" value={form.avsandare_namn} onChange={e => set("avsandare_namn", e.target.value)} className="input-mod" />
        </Field>

        <Field label="Kontaktväg">
          <input value={form.kontakt_epost} onChange={e => set("kontakt_epost", e.target.value)} placeholder="E-post" className="input-mod mb-2" />
          <input value={form.kontakt_telefon} onChange={e => set("kontakt_telefon", e.target.value)} placeholder="Telefon" className="input-mod" />
        </Field>

        {/* Etappe 4.3 — Pflicht-Audit-Felder */}
        <Field label="Ändrad av" error={errors.andrad_av} required>
          <input data-testid="mod-anslag-andrad-av" value={form.andrad_av} onChange={e => set("andrad_av", e.target.value)} placeholder="Ange namn på den som ändrar posten" className="input-mod" />
        </Field>
        <Field label="Ändringsnotering (valfritt)">
          <input value={form.andringsnotering} onChange={e => set("andringsnotering", e.target.value)} placeholder="Kort kommentar om vad som ändrades" className="input-mod" />
        </Field>

        <div className="flex items-center gap-3">
          <input type="checkbox" checked={form.publish} onChange={e => set("publish", e.target.checked)} className="w-5 h-5 accent-pine" />
          <label className="text-base text-ink">Publicera direkt</label>
        </div>

        {errors.submit && <p className="text-falu text-base">{errors.submit}</p>}

        <div className="flex gap-3 mt-2">
          <button type="submit" disabled={busy} data-testid="mod-create-submit" className="bg-pine hover:bg-pine-hover text-white font-medium px-6 py-3 rounded-md text-base transition-colors disabled:opacity-50">
            {busy ? "Skapar..." : form.publish ? "Skapa och publicera" : "Skapa som utkast"}
          </button>
          <button type="button" onClick={() => navigate("/mod")} className="text-muted-foreground hover:text-ink px-4 py-3 text-base">Avbryt</button>
        </div>
      </form>
    </div>
  );
}

function Field({ label, error, required, children }) {
  return (
    <div>
      <label className="block text-sm font-medium text-ink mb-1">
        {label}{required && <span className="text-falu ml-1">*</span>}
      </label>
      {children}
      {error && <p className="text-falu text-sm mt-1">{error}</p>}
    </div>
  );
}
